import { useMemo, useRef, useState, useEffect } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'

function getDefaultLoadingText(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'sxOw2V8Ril_OM2sxVL-e',
    defaultMessage: 'Finding the best offers for you',
    description: 'Loading text shown while searching for calendar recommendations',
  })
}

function getCheckingDatesText(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'l9jaqaI86j3E0pKRKQkv',
    defaultMessage: 'Checking your dates',
    description: 'Loading text shown while verifying date availability for calendar recommendations',
  })
}

function getAvailabilityText(room: App.Occupants | undefined, intl: IntlShape): string {
  if (!room) {
    return intl.formatMessage({
      id: 'wIrfc_DRfsEsKGZGqhf7',
      defaultMessage: 'Checking room availability',
      description: 'Loading text when checking room availability without occupant details',
    })
  }

  const hasAdults = room.adults > 0
  const hasChildren = !!room.childrenAge?.length

  if (hasAdults && hasChildren) {
    const adultsLabel = intl.formatMessage({
      id: 'V4Nh6rb5GeSYdOFEqt6M',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Adult count for room availability loading text on the offer detail page',
    }, { count: room.adults })
    const childrenLabel = intl.formatMessage({
      id: 'BCePBCfV0hQysprVdkzm',
      defaultMessage: '{count, plural, one {# child} other {# children}}',
      description: 'Children count for room availability loading text on the offer detail page',
    }, { count: room.childrenAge!.length })
    return intl.formatMessage({
      id: 'uqLdE2t9-fEsw3VVrtC-',
      defaultMessage: 'Checking room availability for {guests}',
      description: 'Loading text when checking room availability for adults and children',
    }, { guests: `${adultsLabel}, ${childrenLabel}` })
  }
  if (hasAdults) {
    return intl.formatMessage({
      id: 'AZsftuCtpsqQ3jZUpyuq',
      defaultMessage: 'Checking room availability for {adultCount, plural, one {# adult} other {# adults}}',
      description: 'Loading text when checking room availability for adults only',
    }, { adultCount: room.adults })
  }
  if (hasChildren) {
    return intl.formatMessage({
      id: 'TX4wFy_jLjpxYiGY5vxD',
      defaultMessage: 'Checking room availability for {childCount, plural, one {# child} other {# children}}',
      description: 'Loading text when checking room availability for children only',
    }, { childCount: room.childrenAge!.length })
  }

  return intl.formatMessage({
    id: 'wIrfc_DRfsEsKGZGqhf7',
    defaultMessage: 'Checking room availability',
    description: 'Loading text when checking room availability without occupant details',
  })
}

const MS_BETWEEN_LOADING_PHASES = 3500

interface Props {
  rooms?: Array<App.Occupants>
  onNewPhaseStarted?: () => void
}

const LOADING_PHASE_COUNT = 3

function CalendarPickerRecommendationsLoadingIndicator(props: Props) {
  const { onNewPhaseStarted, rooms } = props
  const intl = useIntl()
  const loadingTransitionScheduler = useRef<NodeJS.Timeout | null>(null)
  const [loadingPhase, setLoadingPhase] = useState<number>(0)

  const text = useMemo(() => {
    switch (loadingPhase) {
      case 0:
        return getDefaultLoadingText(intl)
      case 1:
        return getCheckingDatesText(intl)
      case 2:
        return getAvailabilityText(rooms?.[0], intl)
      default:
        return getDefaultLoadingText(intl)
    }
  }, [loadingPhase, rooms, intl])

  useEffect(() => {
    loadingTransitionScheduler.current = setTimeout(() => {
      if (loadingPhase < LOADING_PHASE_COUNT - 1) {
        setLoadingPhase(loadingPhase + 1)
        loadingTransitionScheduler.current = null
        if (onNewPhaseStarted) onNewPhaseStarted()
      }
    }, MS_BETWEEN_LOADING_PHASES)

    return () => {
      if (loadingTransitionScheduler.current) {
        clearTimeout(loadingTransitionScheduler.current)
      }
    }
  }, [loadingPhase, onNewPhaseStarted])

  return (
    <LoadingIndicator data-testid="loading-indicator" floating backdrop="grey-light">
      <BodyText align="center" colour="neutral-one" variant="large" weight="bold">{text}</BodyText>
    </LoadingIndicator>
  )
}

export default CalendarPickerRecommendationsLoadingIndicator
